<template>
    <div class="mdlist-details" v-if="mdList">
      <div class="header">
        <h2>{{ isEditingMdList ? "Edit Template" : mdList.name }}</h2>
        <div class="top-buttons">
          <button v-if="!isEditingMdList" @click="isEditingMdList = true" class="icon-btn">
            <i class="bi bi-pencil"></i>
          </button>
          <button class="remove-btn" @click="deleteMdList(mdList.id)">
          <i class="bi bi-trash"></i>
        </button>
        </div>
      </div>
  
      <!-- MDList Details -->
      <div class="details">
        <!-- Display mode -->
        <div v-if="!isEditingMdList">
          <div class="detail"><strong>Name:</strong> {{ mdList.name }}</div>
          <div class="detail"><strong>Description:</strong> {{ mdList.description || '-' }}</div>
          <div class="detail"><strong>Entity Type:</strong> {{ selectedEntityType }}</div>
        </div>
  
        <!-- Edit mode -->
        <div v-else>
          <div class="form-group">
            <label for="name">Name</label>
            <input type="text" id="name" v-model="editMdList.name" />
          </div>
          <div class="form-group">
            <label for="description">Description</label>
            <input type="text" id="description" v-model="editMdList.description" />
          </div>
          <div class="buttons-container">
            <button @click="saveMdListChanges">Save</button>
            <button @click="cancelMdListChanges">Cancel</button>
          </div>
        </div>
      </div>
  
      <!-- Keys Table -->
      <div class="keys">
        <h4>Attributes</h4>
        <table class="keys-table">
          <thead>
            <tr>
              <th class="order-header"><span class="visually-hidden">Order</span></th>
              <th>Attribute</th>
              <th>Type</th>
              <th>Unit</th>
              <th>Default</th>
              <th class="actions-header">
                <div class="header-action-buttons">
                  <span>Actions</span>
                  <template v-if="!isEditingKeys">
                    <button @click="enableAllKeyEdits" class="icon-btn" title="Edit all attributes">
                      <i class="bi bi-pencil"></i>
                    </button>
                  </template>
                  <template v-else>
                    <button @click="saveAllKeys" class="btn save-btn">Save</button>
                    <button @click="cancelAllKeyEdits" class="btn cancel-btn">Cancel</button>
                  </template>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(key, index) in keys" :key="key.id">
              <td class="order-cell">
                <button
                  type="button"
                  class="order-btn"
                  :disabled="isReordering || index === 0"
                  :aria-label="`Move ${key.name} up`"
                  @click="moveKey(index, -1)"
                >
                  <i class="bi bi-chevron-up"></i>
                </button>
                <button
                  type="button"
                  class="order-btn"
                  :disabled="isReordering || index === keys.length - 1"
                  :aria-label="`Move ${key.name} down`"
                  @click="moveKey(index, 1)"
                >
                  <i class="bi bi-chevron-down"></i>
                </button>
              </td>
              <td>
                <span v-if="!isEditingKeys">{{ key.name }}</span>
                <input v-else type="text" v-model="key.editKey" />
              </td>
              <td>
                <span v-if="!isEditingKeys">{{ key.value_type || VALUE_TYPES.STRING }}</span>
                <select v-else v-model="key.editValue_type">
                  <option v-for="vt in valueTypes" :key="vt.value" :value="vt.value">
                    {{ vt.label }}
                  </option>
                </select>
              </td>
              <td>
                <span v-if="!isEditingKeys">{{ key.unit || "-" }}</span>
                <input v-else type="text" v-model="key.editUnit" />
              </td>
              <td>
                <span v-if="!isEditingKeys">{{ key.is_default ? "Yes" : "No" }}</span>
                <input v-else type="checkbox" v-model="key.editDefault" />
              </td>
              <td>
                <div class="action-buttons row-actions">
                  <button class="remove-btn" @click="deleteKey(key.id)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
  
      <div class="attribute-builder">
        <div class="attribute-builder__header">
          <h4>Build Template Attributes<HelpLink topic="templates" section="default-and-non-default-attributes" title="Default vs. non-default attributes" /></h4>
          <p>
            Reuse existing attributes for consistency, or create a new one when needed.
          </p>
        </div>

        <div class="attribute-builder__grid">
          <section class="builder-card">
            <div class="builder-card__header">
              <h5>Reuse Existing Attribute</h5>
              <span class="builder-card__badge">{{ eligibleExistingKeys.length }} available</span>
            </div>

            <p class="builder-card__description">
              Search across all attributes available for {{ selectedEntityType }} nodes and attach one to this template.
            </p>

            <multiselect
              v-model="selectedExistingKey"
              :options="eligibleExistingKeys"
              :searchable="true"
              :close-on-select="true"
              :allow-empty="true"
              :custom-label="formatExistingKeyOption"
              label="name"
              track-by="id"
              placeholder="Search existing attributes"
            />

            <div v-if="selectedExistingKey" class="selected-key-preview">
              <div><strong>Name:</strong> {{ selectedExistingKey.name }}</div>
              <div><strong>Type:</strong> {{ selectedExistingKey.value_type || VALUE_TYPES.STRING }}</div>
              <div><strong>Unit:</strong> {{ selectedExistingKey.unit || '-' }}</div>
              <div><strong>Default:</strong> {{ selectedExistingKey.is_default ? 'Yes' : 'No' }}</div>
            </div>

            <p v-else-if="!eligibleExistingKeys.length" class="empty-helper">
              All available attributes are already attached to this template.
            </p>

            <div class="builder-card__actions">
              <button
                class="primary-btn"
                :disabled="!selectedExistingKey"
                @click="attachExistingKey"
              >
                Add Existing Attribute
              </button>
            </div>
          </section>

          <section class="builder-card">
            <div class="builder-card__header">
              <h5>Create New Attribute</h5>
              <span class="builder-card__badge builder-card__badge--accent">New</span>
            </div>

            <p class="builder-card__description">
              Create a brand-new reusable attribute and attach it to this template immediately.
            </p>

            <div class="builder-form-grid">
              <div class="form-field form-field--full">
                <label for="new-key-name">Attribute</label>
                <input id="new-key-name" type="text" v-model="newKey.key" placeholder="e.g. Concentration" />
              </div>

              <div class="form-field">
                <label for="new-key-type">Type</label>
                <select id="new-key-type" v-model="newKey.value_type">
                  <option v-for="vt in valueTypes" :key="vt.value" :value="vt.value">
                    {{ vt.label }}
                  </option>
                </select>
              </div>

              <div class="form-field">
                <label for="new-key-unit">Unit</label>
                <input id="new-key-unit" type="text" v-model="newKey.unit" placeholder="Optional" />
              </div>

              <label class="checkbox-field form-field--full">
                <input type="checkbox" v-model="newKey.default" />
                <span>Mark as default attribute</span>
              </label>
            </div>

            <div class="builder-card__actions">
              <button class="primary-btn" @click="addKey">Create and Add Attribute</button>
            </div>
          </section>
        </div>
      </div>
    </div>
    <div v-else>
      <p>Loading Template...</p>
    </div>
  </template>
  
  
  
  
  <script>
  import { useToast } from "vue-toastification";
  import apiService from "@/services/apiService";
  import { API_ENDPOINTS } from "@/services/config";
  import { confirmDelete } from "@/utils/confirmDelete";
  import { normalizeMetadataKeyCollection } from "@/utils/metadataTransforms";
  import { getValueTypes, VALUE_TYPES } from "@/utils/valueTypeCoercion";
  import { mapState } from 'vuex';
  import Multiselect from "vue-multiselect";
  
  export default {
    components: { Multiselect },
    data() {
      return {
        mdList: null,
        keys: [],
        availableKeys: [],
        selectedExistingKey: null,
        isEditingKeys: false,
        isEditingMdList: false,
        isReordering: false,
        editMdList: { name: "", description: "" },
        newKey: { key: "", unit: "", value_type: VALUE_TYPES.STRING, default: true },
        valueTypes: getValueTypes(),
      };
    },
    setup() {
      const toast = useToast();
      return { toast };
    },
    created() {
      this.fetchMdList();
      
    },
    computed: {
    ...mapState(['loggedInUser']), 
    selectedEntityType() {
      return this.$route.query.entity_type || "experiment";
    },
    attachedKeyIds() {
      return new Set(this.keys.map((key) => key.id));
    },
    eligibleExistingKeys() {
      return this.availableKeys
        .filter((key) => !this.attachedKeyIds.has(key.id))
        .sort((left, right) => left.name.localeCompare(right.name));
    },
  },
    methods: {
      buildEditableKeyState(key) {
        return {
          ...key,
          editKey: key.name,
          editValue_type: key.value_type || VALUE_TYPES.STRING,
          editUnit: key.unit,
          editDefault: key.is_default,
        };
      },
      async fetchMdList() {
        try {
          const response = await apiService.get(
            API_ENDPOINTS.metadataList(this.$route.params.id),
            {},
            this.loggedInUser?.entra_id
          );
          this.mdList = response;
          this.editMdList = { ...response };
          await Promise.all([this.fetchKeys(), this.fetchAvailableKeys()]);
        } catch (error) {
          this.toast.error("Failed to fetch metadata list details.");
          console.error(error);
        }
      },
      async fetchKeys() {
        try {
          const response = await apiService.get(
            API_ENDPOINTS.mdkeys(this.$route.params.id),
            { entity_type: this.selectedEntityType },
            this.loggedInUser?.entra_id
          );
          this.keys = normalizeMetadataKeyCollection(response).map((key) => this.buildEditableKeyState(key));
          this.selectedExistingKey = this.selectedExistingKey && this.attachedKeyIds.has(this.selectedExistingKey.id)
            ? null
            : this.selectedExistingKey;
          this.isEditingKeys = false;
        } catch (error) {
          this.toast.error("Failed to fetch keys.");
          console.error(error);
        }
      },
      async fetchAvailableKeys() {
        try {
          const response = await apiService.get(
            API_ENDPOINTS.metadataKeys,
            { entity_type: this.selectedEntityType },
            this.loggedInUser?.entra_id
          );
          this.availableKeys = normalizeMetadataKeyCollection(response);
        } catch (error) {
          this.toast.error("Failed to fetch reusable attributes.");
          console.error(error);
        }
      },
      formatExistingKeyOption(key) {
        if (!key) {
          return "";
        }

        const typeLabel = key.value_type || VALUE_TYPES.STRING;
        const unitLabel = key.unit ? ` · ${key.unit}` : "";
        return `${key.name} (${typeLabel}${unitLabel})`;
      },
      async moveKey(index, direction) {
        const target = index + direction;
        if (this.isReordering || target < 0 || target >= this.keys.length) return;

        const previous = this.keys;
        const reordered = previous.slice();
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

        // Existing sort_order values may be duplicated (e.g. all 0), so renumber by position.
        const changed = [];
        this.keys = reordered.map((key, position) => {
          if (key.sort_order === position) return key;
          changed.push({ id: key.id, sort_order: position });
          return { ...key, sort_order: position };
        });

        this.isReordering = true;
        try {
          await Promise.all(
            changed.map(({ id, sort_order }) =>
              apiService.updateResource(
                API_ENDPOINTS.metadataKey(id),
                { sort_order },
                this.loggedInUser?.id,
                this.loggedInUser?.entra_id
              )
            )
          );
        } catch (error) {
          this.keys = previous;
          this.toast.error("Failed to change attribute order.");
          console.error(error);
        } finally {
          this.isReordering = false;
        }
      },
      async saveMdListChanges() {
        try {
          await apiService.updateResource(API_ENDPOINTS.metadataList(this.mdList.id), {
            name: this.editMdList.name,
            description: this.editMdList.description || null,
          }, this.loggedInUser?.id, this.loggedInUser?.entra_id);
          this.mdList = { ...this.editMdList };
          this.isEditingMdList = false;
          this.toast.success("Metadata list updated successfully.");
        } catch (error) {
          this.toast.error("Failed to update metadata list.");
          console.error(error);
        }
      },
      cancelMdListChanges() {
        this.editMdList = { ...this.mdList };
        this.isEditingMdList = false;
      },
      enableAllKeyEdits() {
        this.isEditingKeys = true;
        this.keys = this.keys.map((key) => this.buildEditableKeyState(key));
      },
      cancelAllKeyEdits() {
        this.keys = this.keys.map((key) => this.buildEditableKeyState(key));
        this.isEditingKeys = false;
      },
      async saveAllKeys() {
        try {
          await Promise.all(
            this.keys.map((key) => {
              const updatedKey = {
                name: key.editKey,
                value_type: key.editValue_type,
                unit: key.editUnit,
                is_default: key.editDefault,
              };

              return apiService.updateResource(
                API_ENDPOINTS.metadataKey(key.id),
                updatedKey,
                this.loggedInUser?.id,
                this.loggedInUser?.entra_id
              );
            })
          );

          this.toast.success("Attributes updated successfully.");
          await this.fetchKeys();
        } catch (error) {
          this.toast.error("Failed to update attributes.");
          console.error(error);
        }
      },
      async deleteMdList(listId) {
        try {
          const isConfirmed = await confirmDelete("Are you sure you want to delete this Metadata list?");
        if (!isConfirmed) return; // Exit if the user cancels

          await apiService.deleteResource(API_ENDPOINTS.metadataList(listId), this.loggedInUser.id, this.loggedInUser?.entra_id);
          this.$router.push("/experiments");
          this.toast.success("Metadata list deleted successfully.");
        } catch (error) {
          this.toast.error("Failed to delete metadata list.");
          console.error(error);
        }
      },
      async deleteKey(keyId) {
        try {
          const isConfirmed = await confirmDelete("Are you sure you want to delete this attribute?");
          if (!isConfirmed) return;

          await apiService.deleteResource(API_ENDPOINTS.metadataKey(keyId), this.loggedInUser.id, this.loggedInUser?.entra_id);
          this.toast.success("Key deleted successfully.");
          this.keys = this.keys.filter((key) => key.id !== keyId);
          this.selectedExistingKey = null;
        } catch (error) {
          this.toast.error("Failed to delete key.");
          console.error(error);
        }
      },
      async attachExistingKey() {
        if (!this.selectedExistingKey) {
          this.toast.error("Select an existing attribute first.");
          return;
        }

        try {
          await apiService.createResource(
            API_ENDPOINTS.metadataListKeys(this.mdList.id),
            {
              metadata_key_id: this.selectedExistingKey.id,
              is_default: this.selectedExistingKey.is_default,
            },
            this.loggedInUser?.entra_id
          );
          this.toast.success("Existing attribute attached successfully.");
          this.selectedExistingKey = null;
          await this.fetchKeys();
        } catch (error) {
          this.toast.error("Failed to attach existing attribute.");
          console.error(error);
        }
      },
      async addKey() {
        if (!this.newKey.key) {
          this.toast.error("Key field cannot be empty.");
          return;
        }
        try {
          const newKeyData = {
            entity_type: this.selectedEntityType,
            name: this.newKey.key,
            value_type: this.newKey.value_type,
            unit: this.newKey.unit,
            is_default: this.newKey.default,
          };
          await apiService.createResource(API_ENDPOINTS.mdkeys(this.mdList.id), newKeyData, this.loggedInUser?.entra_id);
          this.toast.success("Key added successfully.");
          this.newKey = { key: "", unit: "", value_type: VALUE_TYPES.STRING, default: true };
          await Promise.all([this.fetchKeys(), this.fetchAvailableKeys()]);
        } catch (error) {
          this.toast.error("Failed to add key.");
          console.error(error);
        }
      },
    },
  };
  </script>
  
  <style scoped lang="scss">
@import "@/assets/variables.scss";

.mdlist-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    color: $primary-color;
    margin-bottom: 20px;
  }
}

.top-buttons {
      display: flex;
      gap: 10px;
    }

.details,
.keys {
  margin-bottom: 20px;
}

.keys-table {
  width: 100%;
  border-collapse: collapse;
}

.keys-table th,
.keys-table td {
  padding: 10px;
  border: 1px solid #ddd;
}

.keys-table td input,
.keys-table td select {
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
}

.keys-table td .action-buttons {
  display: flex; /* Align buttons side by side */
  gap: 10px; /* Add spacing between the buttons */
}

.btn {
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  padding: 5px 10px;
}

.save-btn {
  background-color: $primary-color;
}

.save-btn:hover {
  background-color: darken($primary-color, 10%);
}

.cancel-btn {
  background-color: #6c757d;
}

.cancel-btn:hover {
  background-color: #5a6268;
}

.actions-header {
  width: 72px;
  min-width: 72px;
  text-align: center;
}

.header-action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.row-actions {
  justify-content: center;
}

.header-action-buttons span {
  display: none;
}

.order-header,
.order-cell {
  width: 56px;
  min-width: 56px;
  text-align: center;
  white-space: nowrap;
}

.order-btn {
  padding: 2px 6px;
  margin: 0 1px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  color: $primary-color;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba($primary-color, 0.12);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.keys-table th:last-child,
.keys-table td:last-child {
  width: 72px;
  min-width: 72px;
  text-align: center;
}

.attribute-builder {
  margin-top: 20px;

  &__header {
    margin-bottom: 16px;

    h4 {
      margin-bottom: 4px;
    }

    p {
      margin: 0;
      color: rgba(0, 0, 0, 0.65);
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
  }
}

.builder-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    h5 {
      margin: 0;
      color: $primary-color;
    }
  }

  &__description {
    margin: 0 0 12px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;
    line-height: 1.4;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    background: rgba($primary-color, 0.12);
    color: $primary-color;

    &--accent {
      background: rgba(46, 204, 113, 0.14);
      color: #1e8449;
    }
  }

  &__actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }
}

.selected-key-preview {
  margin-top: 12px;
  padding: 12px;
  border-radius: 10px;
  background: rgba($primary-color, 0.06);
  display: grid;
  gap: 6px;
  font-size: 14px;
}

.empty-helper {
  margin: 12px 0 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}

.builder-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &--full {
    grid-column: 1 / -1;
  }

  label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.7);
  }

  input,
  select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d4d8dd;
    border-radius: 8px;
    font-size: 14px;
    background: #fff;
  }
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;

  input {
    width: auto;
  }
}

.primary-btn {
  padding: 10px 14px;
  background-color: $primary-color;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;

  &:hover:not(:disabled) {
    background-color: darken($primary-color, 10%);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

:deep(.multiselect) {
  min-height: 42px;
}

:deep(.multiselect__tags) {
  border-radius: 8px;
  border-color: #d4d8dd;
  min-height: 42px;
}

:deep(.multiselect__option--highlight),
:deep(.multiselect__option--selected.multiselect__option--highlight) {
  background: $primary-color;
}

@media (max-width: 768px) {
  .builder-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
  